import { CARD_COLORS, EVENT_COLORS, hexToRgbString } from '@/shared/constants/colorPalette'

const DARK_RGB_BY_LIGHT_RGB = {}
const COLOR_NAME_BY_LIGHT_RGB = {}

for (const c of [...CARD_COLORS, ...EVENT_COLORS]) {
  const lightRgb = hexToRgbString(c.light)
  DARK_RGB_BY_LIGHT_RGB[lightRgb] = hexToRgbString(c.dark)
  COLOR_NAME_BY_LIGHT_RGB[lightRgb] = c.name
}

export const getDarkThemeColor = (rgb) => {
  const normalized = rgb.replace(/\s+/g, '')
  return DARK_RGB_BY_LIGHT_RGB[normalized] || adjustColorForDarkTheme(rgb)
}

export const getColorName = (rgb) => {
  const normalized = rgb.replace(/\s+/g, '')
  return COLOR_NAME_BY_LIGHT_RGB[normalized] || null
}

const hslChannel = (h, s, l, n) => {
  const k = (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
}

export const parseColor = (color) => {
  if (Array.isArray(color) && (color.length === 3 || color.length === 4)) {
    return color
  }

  if (color.startsWith('rgb')) {
    color = color.replace('rgb(', '')
    color = color.replace(')', '')
    let [r, g, b] = color.split(',')
    r /= 255
    g /= 255
    b /= 255
    return [r, g, b]
  }

  if (color.startsWith('hsl')) {
    const match = color.match(/hsl\(\s*(-?[\d.]+)(?:deg)?\s*,\s*([\d.]+)%?\s*,\s*([\d.]+)%?\s*\)/)
    if (match) {
      const h = parseFloat(match[1])
      const s = parseFloat(match[2]) / 100
      const l = parseFloat(match[3]) / 100
      return [hslChannel(h, s, l, 0), hslChannel(h, s, l, 8), hslChannel(h, s, l, 4)]
    }
  }

  console.error('Color format not supported')
  return [0, 0, 0]
}

export const rgbToHsl = ([r, g, b]) => {
  const l = Math.max(r, g, b)
  const s = l - Math.min(r, g, b)
  const h = s
    ? l === r
      ? (g - b) / s
      : l === g
        ? 2 + (b - r) / s
        : 4 + (r - g) / s
    : 0
  const res = [
    60 * h < 0 ? 60 * h + 360 : 60 * h,
    100 * (s ? (l <= 0.5 ? s / (2 * l - s) : s / (2 - (2 * l - s))) : 0),
    (100 * (2 * l - s)) / 2
  ]
  return res
}

export const darkenColor = (color) => {
  color = rgbToHsl(parseColor(color))
  color[0] = Math.min(Math.max(color[0] + 2, 0), 360)
  color[1] = Math.min(Math.max(color[1] - 64, 0), 100)
  color[2] = Math.min(Math.max(color[2] - 25, 0), 100)
  return `hsl(${color[0]}, ${color[1]}%, ${color[2]}%)`
}

export const lightenColor = (color) => {
  color = rgbToHsl(parseColor(color))
  color[0] = Math.min(Math.max(color[0] - 2, 0), 360)
  color[1] = Math.min(Math.max(color[1] + 34, 0), 100)
  color[2] = Math.min(Math.max(color[2] + Math.floor((100 - color[2]) * 0.8), 0), 100)
  return `hsl(${color[0]}, ${color[1]}%, ${color[2]}%)`
}

const adjustColorForDarkTheme = (rgb) => {
  const hsl = rgbToHsl(parseColor(rgb))
  const isWarmHue = hsl[0] >= 0 && hsl[0] <= 130
  if (isWarmHue) {
    hsl[1] = Math.max(hsl[1] * 0.85, 50)
    hsl[2] = Math.max(Math.min(hsl[2] - 25, 52), 42)
  } else {
    hsl[1] = Math.max(hsl[1] - 10, 15)
    hsl[2] = Math.max(Math.min(hsl[2] - 25, 55), 35)
  }
  return `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`
}

export const selectForegroundColor = (red, green, blue) => {
  if ((red * 0.299 + green * 0.587 + blue * 0.114) > 186) {
    return '#000000'
  } else {
    return '#ffffff'
  }
}
